/**
 * LLM-based intent classification system
 */
import { execSync } from 'child_process';

export interface ClassificationResult {
  intent: string;
  confidence: number;
  extractedEntities: Record<string, string>;
  reasoning?: string;
  classifierType: 'llm' | 'fallback' | 'mock';
  modelUsed: string;
}

export class LLMIntentClassifier {
  private config: {
    provider: 'claude-code' | 'openai' | 'anthropic' | 'mock';
    model: string;
    enabled: boolean;
  };

  private supportedIntents = [
    'SHOW_STORY - Navigate to/display a specific component or story',
    'ANALYZE_STORY - Analyze current story properties, colors, or visual elements', 
    'SEARCH_COMPONENTS - Find/list components by category or functionality',
    'ADD_VARIANT - Create new component variant or modify existing component',
    'CREATE_SECTION - Create entirely new component or section',
    'VALIDATE_DESIGN - Validate design patterns or check compliance',
    'PLAN_MIGRATION - Plan migration between versions',
    'EXECUTE_MIGRATION - Execute planned migration',
    'GENERAL - General conversation or unclear intent'
  ];

  constructor(config?: Partial<typeof LLMIntentClassifier.prototype.config>) {
    // Check if we're in Claude Code context or have API keys available
    const hasClaudeCode = !!process.env.CLAUDE_CODE_AVAILABLE || !!(globalThis as any).claude || this.checkClaudeSubprocess();
    const hasOpenAI = !!process.env.OPENAI_API_KEY;
    const hasAnthropic = !!process.env.ANTHROPIC_API_KEY;

    this.config = {
      provider: hasClaudeCode ? 'claude-code' : hasOpenAI ? 'openai' : hasAnthropic ? 'anthropic' : 'mock',
      model: hasClaudeCode ? 'claude-sonnet-4' : hasOpenAI ? 'gpt-4' : hasAnthropic ? 'claude-3-sonnet' : 'mock-classifier',
      enabled: hasClaudeCode || hasOpenAI || hasAnthropic,
      ...config
    };
  }

  private checkClaudeSubprocess(): boolean {
    try {
      // Quick sync check for Claude CLI availability
      execSync('claude --help', { stdio: 'ignore', timeout: 2000 });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Classify user intent using LLM reasoning
   */
  async classify(message: string): Promise<ClassificationResult> {
    console.log(`🤖 Intent Classification Config: ${this.config.provider} (${this.config.model}) - Enabled: ${this.config.enabled}`);
    
    try {
      if (this.config.enabled && this.config.provider !== 'mock') {
        // Try actual LLM classification
        return await this.classifyWithLLM(message);
      } else {
        // Use mock classification with clear labeling
        return await this.simulateLLMClassification(message);
      }
    } catch (error) {
      console.warn('🚨 LLM classification failed, using fallback:', error);
      // Fallback to simple classification
      return this.fallbackClassification(message);
    }
  }

  private async classifyWithLLM(message: string): Promise<ClassificationResult> {
    const prompt = this.buildClassificationPrompt(message);
    
    switch (this.config.provider) {
      case 'claude-code':
        return await this.classifyWithClaudeCode(prompt, message);
      case 'openai':
        return await this.classifyWithOpenAI(prompt, message);
      case 'anthropic':
        return await this.classifyWithAnthropic(prompt, message);
      default:
        throw new Error(`Unsupported LLM provider: ${this.config.provider}`);
    }
  }

  private async classifyWithClaudeCode(prompt: string, message: string): Promise<ClassificationResult> {
    // Try global API first, then fall back to subprocess
    if ((globalThis as any).claude && typeof (globalThis as any).claude.ask === 'function') {
      try {
        const response = await (globalThis as any).claude.ask(prompt);
        return this.parseClassificationResponse(response, message, 'llm', this.config.model);
      } catch (error) {
        console.warn('Global Claude API failed, trying subprocess:', error);
      }
    }

    // Use subprocess approach
    try {
      const response = await this.callClaudeSubprocess(prompt);
      return this.parseClassificationResponse(response, message, 'llm', this.config.model);
    } catch (error) {
      throw new Error(`Claude Code classification failed: ${error}`);
    }
  }

  private async callClaudeSubprocess(prompt: string): Promise<string> {
    const { spawn } = await import('child_process');
    
    return new Promise((resolve, reject) => {
      const proc = spawn('claude', ['--print'], { stdio: 'pipe' });
      let stdout = '';
      let stderr = '';

      proc.stdin?.write(prompt);
      proc.stdin?.end();

      proc.stdout?.on('data', (data) => {
        stdout += data.toString();
      });

      proc.stderr?.on('data', (data) => {
        stderr += data.toString();
      });

      proc.on('close', (code) => {
        if (code === 0) {
          resolve(stdout.trim());
        } else {
          reject(stderr.trim() || `Claude CLI exited with code ${code}`);
        }
      });

      proc.on('error', (err) => {
        reject(`Failed to spawn Claude CLI: ${err.message}`);
      });

      // Timeout after 15 seconds
      setTimeout(() => {
        proc.kill();
        reject('Claude CLI timeout');
      }, 15000);
    });
  }

  private async classifyWithOpenAI(prompt: string, message: string): Promise<ClassificationResult> {
    // OpenAI integration would go here
    throw new Error('OpenAI integration not implemented yet');
  }

  private async classifyWithAnthropic(prompt: string, message: string): Promise<ClassificationResult> {
    // Anthropic API integration would go here  
    throw new Error('Anthropic API integration not implemented yet');
  }

  private parseClassificationResponse(response: string, originalMessage: string, classifierType: 'llm' | 'fallback' | 'mock', modelUsed: string): ClassificationResult {
    try {
      // Try to extract JSON from Claude response
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        return {
          intent: parsed.intent || 'GENERAL',
          confidence: parsed.confidence || 0.7,
          extractedEntities: parsed.entities || parsed.extractedEntities || {
            componentName: parsed.componentName || null,
            analysisType: parsed.analysisType || null
          },
          classifierType,
          modelUsed,
          reasoning: parsed.reasoning || 'Claude Code classification'
        };
      }
    } catch (error) {
      console.warn('Failed to parse Claude JSON response:', error);
    }

    // Fallback to pattern-based parsing of Claude's natural language response
    return this.extractIntentFromNaturalLanguage(response, originalMessage, classifierType, modelUsed);
  }

  private extractIntentFromNaturalLanguage(response: string, originalMessage: string, classifierType: 'llm' | 'fallback' | 'mock', modelUsed: string): ClassificationResult {
    const lowerResponse = response.toLowerCase();
    const lowerMessage = originalMessage.toLowerCase();
    
    // Extract intent from Claude's natural language response
    let intent = 'GENERAL';
    let confidence = 0.6;
    let extractedEntities: Record<string, any> = {};
    
    if (lowerResponse.includes('show') || lowerResponse.includes('navigate') || lowerMessage.includes('show me')) {
      intent = 'SHOW_STORY';
      confidence = 0.8;
      const componentMatch = originalMessage.match(/\b(button|card|form|input|modal|table|header|footer|nav|menu)\b/i);
      if (componentMatch) {
        extractedEntities.componentName = componentMatch[1].toLowerCase();
      }
    } else if (lowerResponse.includes('analyze') || lowerResponse.includes('why') || lowerMessage.includes('why is') || lowerMessage.includes('what color')) {
      intent = 'ANALYZE_STORY';
      confidence = 0.8;
      if (lowerMessage.includes('color') || lowerMessage.includes('blue') || lowerMessage.includes('red')) {
        extractedEntities.analysisType = 'visual';
      }
    } else if (lowerResponse.includes('find') || lowerResponse.includes('search') || lowerMessage.includes('find')) {
      intent = 'SEARCH_COMPONENTS';
      confidence = 0.8;
      extractedEntities.searchQuery = originalMessage;
    } else if (lowerResponse.includes('create') || lowerResponse.includes('variant') || lowerMessage.includes('create')) {
      intent = 'ADD_VARIANT';
      confidence = 0.7;
    }

    return {
      intent,
      confidence,
      extractedEntities,
      classifierType,
      modelUsed,
      reasoning: `Extracted from Claude response: ${response.slice(0, 100)}...`
    };
  }

  private async simulateLLMClassification(message: string): Promise<ClassificationResult> {
    // Simulate what an LLM would analyze
    const prompt = this.buildClassificationPrompt(message);
    
    // For now, return intelligent classification based on common patterns
    // This simulates LLM reasoning
    const lowerMessage = message.toLowerCase();
    
    // Navigation patterns
    if (this.containsNavigationIntent(lowerMessage)) {
      return {
        intent: 'SHOW_STORY',
        confidence: 0.9,
        extractedEntities: {
          component: this.extractComponentName(lowerMessage)
        },
        reasoning: 'User wants to navigate to or display a component',
        classifierType: 'mock',
        modelUsed: this.config.model
      };
    }

    // Analysis patterns (including colors, properties, visual questions)
    if (this.containsAnalysisIntent(lowerMessage)) {
      return {
        intent: 'ANALYZE_STORY',
        confidence: 0.85,
        extractedEntities: {
          analysisType: this.extractAnalysisType(lowerMessage),
          target: this.extractComponentName(lowerMessage) || 'current'
        },
        reasoning: 'User wants to analyze or understand component properties',
        classifierType: 'mock',
        modelUsed: this.config.model
      };
    }

    // Search patterns
    if (this.containsSearchIntent(lowerMessage)) {
      return {
        intent: 'SEARCH_COMPONENTS',
        confidence: 0.8,
        extractedEntities: {
          searchQuery: this.extractSearchQuery(lowerMessage),
          category: this.extractCategory(lowerMessage)
        },
        reasoning: 'User wants to find or list components',
        classifierType: 'mock',
        modelUsed: this.config.model
      };
    }

    // Creation patterns
    if (this.containsCreationIntent(lowerMessage)) {
      if (lowerMessage.includes('variant')) {
        return {
          intent: 'ADD_VARIANT',
          confidence: 0.75,
          extractedEntities: {
            component: this.extractComponentName(lowerMessage),
            variantType: this.extractVariantType(lowerMessage)
          },
          reasoning: 'User wants to create a component variant',
          classifierType: 'mock',
          modelUsed: this.config.model
        };
      } else {
        return {
          intent: 'CREATE_SECTION',
          confidence: 0.7,
          extractedEntities: {
            component: this.extractComponentName(lowerMessage)
          },
          reasoning: 'User wants to create a new component',
          classifierType: 'mock',
          modelUsed: this.config.model
        };
      }
    }

    // Default to general
    return {
      intent: 'GENERAL',
      confidence: 0.3,
      extractedEntities: {},
      reasoning: 'Intent unclear, treating as general conversation',
      classifierType: 'mock',
      modelUsed: this.config.model
    };
  }

  private buildClassificationPrompt(message: string): string {
    return `
Classify the user's intent from this message: "${message}"

Available intents:
${this.supportedIntents.map(intent => `- ${intent}`).join('\n')}

Return the most appropriate intent, confidence (0-1), and extracted entities.

Examples:
- "Show me the Button component" → SHOW_STORY (component: "Button")
- "What color is this button?" → ANALYZE_STORY (analysisType: "visual")  
- "Find form components" → SEARCH_COMPONENTS (category: "form")
- "Create a large button variant" → ADD_VARIANT (component: "Button", variantType: "large")
    `;
  }

  // Intent detection helpers
  private containsNavigationIntent(message: string): boolean {
    const navWords = ['show', 'display', 'navigate', 'go to', 'open', 'load', 'render'];
    const contextWords = ['component', 'story', 'me the'];
    
    return navWords.some(word => message.includes(word)) && 
           contextWords.some(word => message.includes(word));
  }

  private containsAnalysisIntent(message: string): boolean {
    const analysisWords = ['what', 'why', 'how', 'tell me', 'describe', 'explain', 'analyze'];
    const subjectWords = ['color', 'property', 'style', 'current', 'this', 'button', 'component'];
    
    return analysisWords.some(word => message.includes(word)) &&
           (subjectWords.some(word => message.includes(word)) || 
            message.includes('is') || message.includes('are'));
  }

  private containsSearchIntent(message: string): boolean {
    const searchWords = ['find', 'search', 'look for', 'list', 'show me all'];
    return searchWords.some(word => message.includes(word));
  }

  private containsCreationIntent(message: string): boolean {
    const createWords = ['create', 'add', 'make', 'build', 'new'];
    return createWords.some(word => message.includes(word));
  }

  // Entity extraction helpers
  private extractComponentName(message: string): string {
    const components = ['button', 'card', 'input', 'modal', 'form', 'navigation'];
    const found = components.find(comp => message.includes(comp));
    return found || '';
  }

  private extractAnalysisType(message: string): string {
    if (message.includes('color') || message.includes('blue') || message.includes('style')) {
      return 'visual';
    }
    if (message.includes('prop')) return 'properties';
    return 'general';
  }

  private extractSearchQuery(message: string): string {
    // Extract what they're searching for
    const match = message.match(/(?:find|search|look for)\s+(.+?)(?:\s+component|$)/);
    return match ? match[1].trim() : '';
  }

  private extractCategory(message: string): string {
    const categories = ['form', 'layout', 'navigation', 'overlay'];
    return categories.find(cat => message.includes(cat)) || '';
  }

  private extractVariantType(message: string): string {
    const variants = ['large', 'small', 'primary', 'secondary', 'disabled'];
    return variants.find(variant => message.includes(variant)) || '';
  }

  private fallbackClassification(message: string): ClassificationResult {
    // Simple fallback when LLM fails
    const lowerMessage = message.toLowerCase();
    
    if (lowerMessage.includes('show') || lowerMessage.includes('display')) {
      return {
        intent: 'SHOW_STORY',
        confidence: 0.6,
        extractedEntities: { component: this.extractComponentName(lowerMessage) },
        classifierType: 'fallback',
        modelUsed: 'pattern-matcher'
      };
    }
    
    if (lowerMessage.includes('what') || lowerMessage.includes('color')) {
      return {
        intent: 'ANALYZE_STORY', 
        confidence: 0.6,
        extractedEntities: { analysisType: 'visual' },
        classifierType: 'fallback',
        modelUsed: 'pattern-matcher'
      };
    }
    
    return {
      intent: 'GENERAL',
      confidence: 0.4,
      extractedEntities: {},
      classifierType: 'fallback',
      modelUsed: 'pattern-matcher'
    };
  }

  /**
   * Get supported intents for help/debugging
   */
  getSupportedIntents(): string[] {
    return this.supportedIntents;
  }
}